import {
  HttpClient,
  HttpEvent,
  HttpRequest,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { PublicApi } from '../constants';
import { SecureStorageService } from './secure-storage.service';

// app-alert.model.ts
export interface AppAlert {
  id: number;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
  action?: any;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
 // ✅ AFTER (Correct)
private profileImageSource = new BehaviorSubject<string>(
  this.getInitialProfileImage() // localStorage se load karta hai
);

private getInitialProfileImage(): string {
  const storedImage = localStorage.getItem('profileImage');
  if (storedImage && storedImage !== 'assets/images/profile.png') {
    return storedImage;
  }
  return 'assets/images/profile.png';
}
  profileImage$ = this.profileImageSource.asObservable();
  private planTypeSubject = new BehaviorSubject<'none' | 'basic' | 'advanced'>('none');
  constructor(
    private notification: NzNotificationService,
    private modal: NzModalService,
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) {
    const savedAlerts = localStorage.getItem('app_alerts');
    if (savedAlerts) {
      try {
        const parsed = JSON.parse(savedAlerts);
        this.alertsSubject.next(parsed);
      } catch (e) {
        console.error('Failed to parse saved alerts:', e);
      }
    }
  }

  public updatedUserData: Subject<any> = new Subject();
  private breadcrumbSubject = new BehaviorSubject<any[]>([]);
  breadcrumb$ = this.breadcrumbSubject.asObservable();
  checkHomePage = true;

  private contestSubject = new BehaviorSubject<any>(null); // Stores contest data
  contest$: Observable<any> = this.contestSubject.asObservable(); // Observable for subscribers

  // Function to set contest data
  setContest(data: any) {
    this.contestSubject.next(data);
  }

  // Function to get the latest contest data (can be used if needed)
  getContest(): Observable<any> {
    return this.contest$;
  }
  setBreadcrumb(breadcrumb: any[]) {
    this.breadcrumbSubject.next(breadcrumb);
  }

  closeAllModals(): void {
    this.modal.closeAll();
  }

  // Use this in catch blocks instead of createNotification — automatically ignores
  // EmptyError (thrown by firstValueFrom when interceptor returns EMPTY on session expiry)
  showError(error: any, fallbackMessage = 'An error occurred'): void {
    if (error?.name === 'EmptyError') return;
    this.createNotification('error', error?.error?.message || fallbackMessage);
  }

  createNotification(type: string, message: string): void {
    const title = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    const modalClass = `contest-styled-modal contest-modal-${type}`;

    switch (type.toLowerCase()) {
      case 'success':
        this.modal.success({ nzTitle: title, nzContent: message, nzCentered: true, nzClassName: modalClass, nzOkText: 'OK' });
        break;
      case 'error':
        this.modal.error({ nzTitle: title, nzContent: message, nzCentered: true, nzClassName: modalClass, nzOkText: 'OK' });
        break;
      case 'warning':
        this.modal.warning({ nzTitle: title, nzContent: message, nzCentered: true, nzClassName: modalClass, nzOkText: 'OK' });
        break;
      case 'info':
      default:
        this.modal.info({ nzTitle: title, nzContent: message, nzCentered: true, nzClassName: modalClass, nzOkText: 'OK' });
        break;
    }
  }

  getUserDetails() {
    return this.secureStorage.getItem('userDetails');
  }

  transformKeys(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(this.transformKeys.bind(this));
    } else if (obj !== null && obj.constructor === Object) {
      return Object.keys(obj).reduce((acc, key) => {
        const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[camelCaseKey] = this.transformKeys(obj[key]);
        return acc;
      }, {} as any);
    }
    return obj;
  }

  private platformSubject = new BehaviorSubject<string>('');
  currentPlatform$ = this.platformSubject.asObservable();

  setPlatform(platform: string): void {
    this.platformSubject.next(platform);
    localStorage.setItem('platform', platform);
  }
 updateProfileImage(imageUrl: string) {
  this.profileImageSource.next(imageUrl);
  
  // ✅ Automatically sync with localStorage
  if (imageUrl && imageUrl !== 'assets/images/profile.png') {
    localStorage.setItem('profileImage', imageUrl);
  } else {
    localStorage.removeItem('profileImage');
  }
}

  getPlatform() {
    return localStorage.getItem('platform')?.toString();
  }

  private baseUrl = 'http://your-backend-url/upload'; // Replace with your backend URL

  upload(file: File): Observable<HttpEvent<any>> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    const headers = new HttpHeaders();
    // You can add custom headers if needed
    // headers = headers.append('Content-Type', 'multipart/form-data');

    return this.http.post<any>(this.baseUrl, formData, {
      headers: headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Loader                                   */
  /* -------------------------------------------------------------------------- */
  private loaderSubject = new BehaviorSubject<boolean>(false);
  loaderState = this.loaderSubject.asObservable();

  showLoader() {
    this.loaderSubject.next(true);
  }

  hideLoader() {
    this.loaderSubject.next(false);
  }

  /* -------------------------------------------------------------------------- */
  /*                              Dashboard Mode                                */
  /* -------------------------------------------------------------------------- */
  // Track if we're on dashboard (with sidebar) or public page
  private isDashboardSubject = new BehaviorSubject<boolean>(false);
  isDashboard$ = this.isDashboardSubject.asObservable();

  setDashboardMode(value: boolean) {
    this.isDashboardSubject.next(value);
  }
  setCampaignLocal(value: any) {
    localStorage.setItem('campaignEdit', value);
  }
  getCampaignLocal() {
    const check = localStorage.getItem('campaignEdit');
    if (check && check == 'true') return true;
    return false;
  }

  timeRemaining(createdAt: string): string {
    if (!createdAt) return 'No date provided';

    const targetDate = new Date(createdAt);
    const now = new Date();

    const diff = targetDate.getTime() - now.getTime();

    if (isNaN(targetDate.getTime())) return 'Invalid date';
    if (diff <= 0) return 'Expired';

    const totalMinutes = Math.floor(diff / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);

    return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''
      } left`;
  }

  getCountryRecords(): Promise<any[]> {
    return firstValueFrom(this.http.get<any[]>(PublicApi.GET_COUNTRIES))
      .then((data: any[]) => {
        return data
          .map((country: any) => ({
            name: country.name || '',
            code: country.code?.toLowerCase() || '',
            dial_code: country.dial_code || '',
            phone_number_length: country.phone_number_length || 0
          }))
          .filter((c: any) => c.name && c.code)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        console.error('Error loading country data:', error);
        this.createNotification('error', 'Failed to load country data');
        return [];
      });
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Alerts                                   */
  /* -------------------------------------------------------------------------- */

  private alertsKey = 'app_alerts';
  private alertsSubject = new BehaviorSubject<AppAlert[]>(this.loadAlerts());
  alerts$ = this.alertsSubject.asObservable();
  private alertIdCounter = 1;
  private tempHiddenAlerts = new Set<number>();
  private hiddenAlertIds = new Set<number>();

  private loadAlerts(): AppAlert[] {
    const stored = localStorage.getItem(this.alertsKey);
    const allAlerts: AppAlert[] = stored ? JSON.parse(stored) : [];

    // Only hide alerts currently in the in-memory set
    return allAlerts.filter((alert) => !this.hiddenAlertIds?.has(alert.id));
  }

  private saveAlerts(alerts: AppAlert[]) {
    localStorage.setItem(this.alertsKey, JSON.stringify(alerts));
  }

  private updateStorage(alerts: AppAlert[]) {
    localStorage.setItem('app_alerts', JSON.stringify(alerts));
  }

  showAlert(type: AppAlert['type'], message: string, action?: string) {
    const alert: AppAlert = {
      id: this.alertIdCounter++,
      type,
      message,
      action,
    };

    const updated = [...this.alertsSubject.getValue(), alert];
    this.alertsSubject.next(updated);
    this.updateStorage(updated);
  }

  closeAlert(id: number) {
    this.hiddenAlertIds.add(id); // Temporarily hide
    const updated = this.alertsSubject.getValue().filter((a) => a.id !== id);
    this.alertsSubject.next(updated);
    // DO NOT touch localStorage
  }

  closeAlertByAction(actionType: string) {
    const updated = this.alertsSubject
      .getValue()
      .filter((alert) => alert.action !== actionType);
    this.alertsSubject.next(updated);
    localStorage.setItem('app_alerts', JSON.stringify(updated)); // if you're persisting
  }

  clearAllAlerts() {
    this.alertsSubject.next([]);
    localStorage.removeItem(this.alertsKey);
  }
  getBaseUrl(): string {
    return window.location.origin;
  }

  setPlanType(plan: string) {
    this.planTypeSubject.next(plan as 'none' | 'basic' | 'advanced');
  }

  getPlanType() {
    return this.planTypeSubject.value;
  }

  getPlanTypeObservable(): Observable<'none' | 'basic' | 'advanced'> {
    return this.planTypeSubject.asObservable();
  }
}
